<template>
  <el-form
    ref="rcForm"
    class="rc-form"
    v-bind="form"
    :model="formData"
    :rules="rules"
    @validate="validateHandle"
  >
    <el-row :gutter="form.gutter" class="rc-form-row">
      <el-col
        v-for="(item, index) in formItem"
        :key="item.prop || index"
        :span="item.span"
        :offset="item.offset"
        :class="{ 'width-auto': form.inline && form.closeLayout }"
      >
        <!-- 使用自定义插槽 -->
        <slot v-if="isSlot(item)" :name="item.slotName"></slot>

        <!-- form-item -->
        <rc-form-item
          v-else-if="showFormItem(item, formData)"
          :data="formData"
          :view-data="viewData"
          :status="form.status || 'edit'"
          v-bind="item"
          v-on="$listeners"
        >
          <template v-for="(slotIndex, name) in $scopedSlots" #[name]="slotScope">
            <slot v-bind="slotScope" :name="name"></slot>
          </template>
          <!-- formItem 多项 -->
          <el-row v-if="Array.isArray(item.children)" :gutter="item.gutter">
            <el-col
              v-for="(subItem, subIndex) in item.children"
              :key="`${index}-${subIndex}}`"
              :span="subItem.span"
              :offset="subItem.offset"
              :class="{ 'width-auto': form.inline && form.closeLayout }"
            >
              <!-- 使用自定义插槽 -->
              <slot v-if="isSlot(subItem)" :name="subItem.slotName"></slot>

              <rc-form-item
                v-else-if="showFormItem(subItem, formData)"
                :data="formData"
                :view-data="viewData"
                :status="form.status || 'edit'"
                v-bind="subItem"
                v-on="$listeners"
              >
                <template v-for="(slotSubIndex, name) in $scopedSlots" #[name]="slotScope">
                  <slot :name="name" v-bind="slotScope"></slot>
                </template>
              </rc-form-item>
            </el-col>
          </el-row>
        </rc-form-item>
      </el-col>
      <slot name="search">
        <el-col v-bind="btnListStyle">
          <el-form-item v-if="showBut" class="rc-form-but-list" :style="{ 'justify-content': align }">
            <rc-but-list v-bind="$attrs" :but-data="comButData" type="form" v-on="$listeners" @confirm="handleSearch"></rc-but-list>
          </el-form-item>
        </el-col>
      </slot>
    </el-row>
  </el-form>
</template>

<script>
import { emitFun, strFirstWith, hasOwnProperty, filterEmpty } from "@/utils/utils";
import { $executeCondition, $isNullOrEmpty } from "@/utils/components/helper";
import { mergeButData, rcSearch } from "../RcBase/mixins/slotMixins";
import RcButList from "../RcButList";
import RcFormItem from "../RcFormItem";

export default {
  name: "RcForm",
  components: { RcButList, RcFormItem },
  mixins: [mergeButData, rcSearch],
  props: {
    // form 本身配置项
    form: {
      type: Object,
      default: () => ({ gutter: 0, inline: false })
    },
    // form-item 配置项
    formItem: {
      type: Array,
      default: () => []
    },
    // 校验规则
    rules: {
      type: Object,
      default: () => {}
    },
    // form数据
    data: {
      type: Object,
      default: () => {}
    },
    // form 查看数据
    viewData: {
      type: Object,
      default: () => {}
    },
    butData: Array,
    merge: [Boolean, Function],
    showSearch: {
      type: Boolean,
      default: true
    },
    clearEmptyFields: {
      type: Boolean,
      default: true
    },
    clearCreated: {
      type: Boolean,
      default: false
    },
    clearMounted: {
      type: Boolean,
      default: true
    },
    align: {
      type: String,
      default: "end"
    },
    btnListStyle: {
      type: Object,
      default: () => ({})
    },
    tableRefs: Object,
    validateState: Function
  },
  data() {
    return {
      formData: this.data,
      butDataDef: [{
        prop: "reset",
        valid: false,
        label: this.$t("public.reset")
      }, {
        prop: "submit",
        label: this.$t("public.confirm"),
        type: "primary"
      }],
      showSearchDef: false,
      setValidateState: {}
    };
  },
  computed: {
    refs() {
      return {
        form: this.$refs.rcForm
      };
    },
    comButData() {
      const { butDataDef, butData } = this;
      return this.butDataFn(butDataDef, butData);
    },
    rcForm() {
      return this.refs.form;
    },
    showBut() {
      const { showSearch, showSearchDef } = this;
      return showSearch && showSearchDef && this.showForm && this.form.status !== "view";
    },
    showForm() {
      const { formItem } = this;
      return formItem && formItem.length;
    }
  },
  watch: {
    formItem: {
      handler(val) {
        this.onlySearch(val);
      },
      deep: true,
      immediate: true
    },
    data: {
      handler(val) {
        this.formData = val;
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    this.initClear("mounted");
  },
  created() {
    this.initClear("created");
  },
  methods: {
    // 是否自定义插槽
    isSlot(item) {
      return item.component === "slot";
    },
    // 是否展示formItem
    showFormItem(item, conditionData) {
      return $isNullOrEmpty(item.condition) || $executeCondition(item.condition, conditionData);
    },
    handleSearch(item, index, obj) {
      const validateForm = !hasOwnProperty(item, "valid") || item.valid ? this.validate() : {};
      emitFun({ self: this, emit: false }, { item, index }, { ... obj, refs: this, name: "rcForm", form: validateForm });
    },
    validate() {
      const { clearEmptyFields, formItem, data, formData } = this;
      const current = filterEmpty(formData, clearEmptyFields);
      const submitData = { formItem, data: current, old: data };
      this.rcForm.validate(valid => {
        Object.assign(submitData, { valid });
        if (valid) {
          this.$emit("submit", submitData);
        } else {
          return false;
        }
      });
      return submitData;
    },
    // 获取组件本身的函数方法
    getComponentFunctionMap() {
      return {
        validate: this.rcForm.validate,
        validateField: this.rcForm.validateField,
        resetFields: this.rcForm.resetFields,
        clearValidate: this.rcForm.clearValidate
      };
    },
    submit(item, index, obj) {
      if (this.tableRefs) {
        this.tableRefs.table.refresh(obj && obj.form);
        return obj && obj.form;
      } else {
        return this.validate();
      }
    },
    reset(item, index, obj) {
      this.showForm && this.rcForm.resetFields();
      if (this.tableRefs) {
        const bool = typeof item === "boolean";
        if (bool && !item || !bool) { this.tableRefs.table.refresh({}); }
      }
    },
    initClear(type) {
      const newType = this[`clear${strFirstWith(type)}`];
      this.showForm && newType && this.reset(newType);
    },
    onlySearch(val) {
      this.showSearchDef = val.length > 1 ? true : !this.filterRcSearch(val);
    },
    filterRcSearch(data) {
      return Boolean(data.find(item => item.component === this.rcSearch));
    },
    dialogCancel() {
      this.reset();
    },
    dialogConfirm() {
      return this.submit();
    },
    itemValidProp() {
      return this.formItem.filter(item => hasOwnProperty(item, "rules")).map(stem => stem.prop) || [];
    },
    formValidProp() {
      const rules = this.$attrs.rules || {};
      return Object.keys(rules);
    },
    validProp() {
      const formValidProp = this.formValidProp();
      return formValidProp.length ? formValidProp : this.itemValidProp();
    },
    validateHandle(prop, state, error) {
      this.$set(this.setValidateState, prop, state);
      this.validateState && this.validateState(this.getValidateState());
    },
    getValidateState() {
      const { setValidateState } = this;
      const rulesList = this.validProp();
      const errorRules = rulesList.filter(item => !setValidateState[item]);
      return {
        state: !errorRules.length,
        data: this.setValidateState
      };
    }
  }
};
</script>

<style lang="scss" scoped>
.rc-form {
  flex: 1;
  width: 100%;
}
.rc-form-row {
  display: flex;
  flex-wrap: wrap;
}
.rc-form-but-list {
  display: flex;
}
.width-auto {
  width: auto;
}
</style>
